import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { Modal, ModalHeader, ModalBody } from 'reactstrap'

import { P } from 'components'
import PMBPagination from 'components/PMBPagination'
import { TableComponent as DataTable } from 'components/TableComponent'
import { getInventorySummary } from 'features/public-views/manageStock/API/manageStockAction'
import colors from 'Theme/colors'
import { newFormatThousandSeparator } from 'utils/formatNumber'

import useIsMobile from 'hooks/useIsMobile'
import Skeleton, { TableSkeleton } from 'components/Skeleton'
import './card.css'

const unitColors = {
  kg: '#198754', // green
  pcs: '#007bff', // blue
  buah: '#ffc107', // yellow
  kardus: '#17a2b8', // cyan
  karung: '#6c757d', // gray
  peti: '#343a40', // dark gray
  ikat: '#20c997', // teal
  kotak: '#6610f2', // purple
  liter: '#fd7e14', // orange
  pak: '#6f42c1', // indigo
  lusin: '#795548', // brown
  sak: '#0dcaf0', // light blue
  ekor: '#28a745', // darker green
}

const getItemBadgeStyle = (unit) => {
  const key = (unit || '').toLowerCase()
  const color = unitColors[key] || '#888' // fallback
  return {
    border: `1px solid ${color}`,
    color,
    backgroundColor: '#ffffff',
  }
}

const InventorySummaryCard = ({
  warehouseId,
  groupId,
  isDetail,
  isRefresh,
  setFetchSummary,
  from,
  to,
  method,
  search,
  sortBy,
  sortDir,
  projectId,
  forpage,
  totalItemsInventory,
  userId,
}) => {
  const dispatch = useDispatch()
  const {
    inventorySummary: reduxSummary,
    inventorySummaryAll: reduxSummaryAll,
    loadingSummary: isLoadingSummary,
  } = useSelector((s) => s.manageStock)
  const [modalOpen, setModalOpen] = useState(false)
  const [isFetchingPage, setIsFetchingPage] = useState(false)
  const isMobile = useIsMobile()

  const containerRef = useRef()
  const [pageSummary, setPageSummary] = useState(0)
  const [sizeSummary, setSizeSummary] = useState(10)
  const [internalSortColumn, setInternalSortColumn] = useState(
    sortBy || 'total',
  )
  const [internalSortDirection, setInternalSortDirection] = useState(
    sortDir || 'desc',
  )
  const summaryPromiseRef = useRef(null)
  const allSummaryPromiseRef = useRef(null)

  useEffect(() => {
    return () => {
      if (summaryPromiseRef.current) summaryPromiseRef.current.abort()
      if (allSummaryPromiseRef.current) allSummaryPromiseRef.current.abort()
    }
  }, [])

  const shouldSkip = isDetail && !groupId

  const lastParamsRef = useRef('')

  const fetchSummaryData = (force = false) => {
    if (shouldSkip) return

    const params = {
      pageSummary,
      sizeSummary,
      internalSortColumn,
      internalSortDirection,
      from,
      to,
      warehouseId,
      groupId,
      method,
      search,
      projectId,
      userId,
      forpage,
    }

    const paramsString = JSON.stringify(params)
    if (!force && lastParamsRef.current === paramsString) {
      return
    }
    lastParamsRef.current = paramsString

    // Abort previous requests
    if (summaryPromiseRef.current) summaryPromiseRef.current.abort()
    if (allSummaryPromiseRef.current) allSummaryPromiseRef.current.abort()

    // Fetch paginated data only if modal is open
    if (modalOpen) {
      setIsFetchingPage(true)
      summaryPromiseRef.current = dispatch(
        getInventorySummary({
          page: pageSummary,
          size: sizeSummary,
          search: search || '',
          sortBy: internalSortColumn,
          sortDir: internalSortDirection,
          groupId: groupId ?? 'null',
          warehouseId: warehouseId ?? 'null',
          dateFrom: from ?? null,
          dateTo: to ?? null,
          method: method || null,
          userId: userId || null,
          forpage: forpage,
          projectId: projectId || null,
        }),
      )
      summaryPromiseRef.current.then(() => setIsFetchingPage(false)).catch(() => setIsFetchingPage(false))
    }

    // Fetch all data for total summary
    allSummaryPromiseRef.current = dispatch(
      getInventorySummary({
        page: 0,
        size: 9999,
        search: search || '',
        sortBy: internalSortColumn,
        sortDir: internalSortDirection,
        groupId: groupId ?? 'null',
        warehouseId: warehouseId ?? 'null',
        dateFrom: from ?? null,
        dateTo: to ?? null,
        method: method || null,
        userId: userId || null,
        forpage: forpage,
        projectId: projectId || null,
        all: true,
      }),
    )
  }

  // Effect for filter changes (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSummaryData()
    }, 600)

    return () => clearTimeout(timer)
  }, [
    pageSummary,
    sizeSummary,
    internalSortColumn,
    internalSortDirection,
    from,
    to,
    warehouseId,
    groupId,
    shouldSkip,
    method,
    search,
    projectId,
    userId,
    modalOpen,
  ])

  // Effect for manual refresh (e.g. from parent after main table fetch)
  useEffect(() => {
    if (isRefresh && !shouldSkip) {
      fetchSummaryData(true)
      setFetchSummary(false)
    }
  }, [isRefresh, shouldSkip])

  const toggleModal = () => {
    setModalOpen(!modalOpen)
    setPageSummary(0)
  }

  const columns = [
    {
      name: <span>Nama Barang</span>,
      selector: 'nameItem',
      sortable: true,
      cell: (row) => (
        <P size="regular" className="text-start mb-0">
          {row?.nameItem}
        </P>
      ),
      style: { justifyContent: 'flex-start' },
    },
    {
      name: <span>Jumlah</span>,
      selector: 'total',
      sortable: true,
      cell: (row) => (
        <P size="regular" className="text-end mb-0">
          {row?.total?.toLocaleString()} {row?.unit}
        </P>
      ),
      style: { justifyContent: 'flex-end' },
      right: true,
    },
    {
      name: <span>Bundle</span>,
      selector: 'totalInventory',
      sortable: true,
      cell: (row) => (
        <P size="regular" className="text-end mb-0">
          {row?.totalInventory?.toLocaleString()}
        </P>
      ),
      style: { justifyContent: 'flex-end' },
      right: true,
    },
  ]

  const allDataSummary = reduxSummaryAll
  const dataSummary = (modalOpen && reduxSummary) ? reduxSummary : reduxSummaryAll

  const summary = useMemo(() => {
    const items = allDataSummary?.items || []

    const totalKgAmount = items.reduce((sum, item) => {
      if (item?.unit?.toLowerCase() === 'kg') {
        return sum + (item?.total ?? 0)
      }
      return sum
    }, 0)

    const totalItemDetailCount = allDataSummary?.totalItems || 0
    const totalTransaction = 0

    return {
      totalKgAmount,
      totalItemDetailCount,
      totalTransaction,
    }
  }, [allDataSummary])

  const handleSort = (column, direction) => {
    if (!column.selector) return
    setInternalSortColumn(column.selector)
    setInternalSortDirection(direction)
  }

  return (
    <>
      <div
        style={{
          padding: '12px',
          backgroundColor: colors.primary[400],
          color: 'white',
          display: 'flex',
          marginTop: '1rem',
          borderRadius: '8px',
          gap: 10,
        }}
      >
        <P>
          Total: {newFormatThousandSeparator(summary.totalKgAmount ?? 0, 2)} Unit+
        </P>
        <P>
          ({newFormatThousandSeparator(totalItemsInventory ?? 0, 2)} Barang)
        </P>
      </div>
      <div className="card shadow-sm rounded-3 mb-3 border-0">
        <div className="card-body p-3">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <P size="regular" bold>
                Ringkasan Inventori
              </P>
              <P className="text-muted" size="regular">
                {newFormatThousandSeparator(totalItemsInventory ?? 0, 2)} entri
                dari{' '}
                {newFormatThousandSeparator(
                  summary?.totalItemDetailCount ?? 0,
                  2,
                )}{' '}
                barang dengan beberapa unit
              </P>
            </div>
          </div>

          {isLoadingSummary ? (
            <div className="d-flex gap-2 overflow-hidden">
              {[...Array(5)].map((_, i) => (
                <Skeleton
                  key={i}
                  width="120px"
                  height="60px"
                  borderRadius="8px"
                />
              ))}
            </div>
          ) : (
            <div
              ref={containerRef}
              className={`d-flex gap-2 ${isMobile ? 'overflow-auto flex-nowrap hide-scrollbar' : 'flex-wrap'}`}
            >
              {allDataSummary?.items?.slice(0, isMobile ? 3 : 10)?.map((item) => (
                <div
                  key={`${item.nameItem}-${item.unit}`}
                  className="badge small d-flex flex-column align-items-start"
                  style={{
                    ...getItemBadgeStyle(item.unit),
                    padding: 8,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <P size="regular" bold className="mb-0">
                    {item.nameItem}
                  </P>
                  <P size="regular" className="py-1 mb-0">
                    {item?.total?.toLocaleString()} {item.unit}
                  </P>
                  {item.totalInventory !== undefined && (
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {item.totalInventory} bundle
                    </div>
                  )}
                </div>
              ))}

              <button
                type="button"
                className="btn btn-outline-primary"
                style={{ whiteSpace: 'nowrap' }}
                onClick={toggleModal}
              >
                <P size="regular">Lihat selengkapnya</P>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal Detail */}
      <Modal
        isOpen={modalOpen}
        toggle={toggleModal}
        scrollable
        centered
        size="lg"
        className="mx-auto"
      >
        <ModalHeader toggle={toggleModal}>
          <P bold>Semua Barang</P>
        </ModalHeader>

        <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }} className={isMobile ? "p-3 bg-light" : "p-3"}>
          {isMobile ? (
            <div className="d-flex flex-column gap-2">
              {dataSummary?.items?.map((item, idx) => (
                <div key={idx} className="card shadow-sm border-0 rounded-3">
                  <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                      <P size="small" className="text-muted mb-0">Nama Barang</P>
                      <P size="regular" bold className="mb-0 text-end text-dark">{item?.nameItem}</P>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                      <P size="small" className="text-muted mb-0">Jumlah</P>
                      <P size="regular" bold className="mb-0 text-end text-dark">{item?.total?.toLocaleString()} {item?.unit}</P>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <P size="small" className="text-muted mb-0">Bundle</P>
                      <P size="regular" bold className="mb-0 text-end text-dark">{item?.totalInventory?.toLocaleString()}</P>
                    </div>
                  </div>
                </div>
              ))}
              
              {isLoadingSummary || isFetchingPage ? (
                <div className="d-flex justify-content-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (!dataSummary?.items || dataSummary?.items?.length === 0) ? (
                <div className="text-center py-4 text-muted">
                  Belum ada data. Buat transaksi untuk memulai.
                </div>
              ) : null}

              {dataSummary?.totalItems > 0 && (
                <div className="mt-2 card shadow-sm border-0 rounded-3 p-3 bg-white">
                  <PMBPagination
                    rowCount={dataSummary?.totalItems}
                    currentPage={dataSummary?.currentPage}
                    rowsPerPage={sizeSummary ?? 10}
                    setCurrentPage={(e) => setPageSummary(e)}
                    setRowsPerPage={(e) => setSizeSummary(e)}
                  />
                </div>
              )}
            </div>
          ) : (
            <div>
              <DataTable
                columns={columns}
                data={dataSummary?.items}
                responsive
                persistTableHead
                progressPending={isLoadingSummary || isFetchingPage}
                progressComponent={<TableSkeleton rows={10} />}
                sortIcon={<ChevronDown />}
                sortServer
                defaultSortFieldId={1}
                defaultSortAsc={false}
                onSort={handleSort}
                className="react-dataTable"
                noDataComponent={
                  <span className="my-1">
                    Belum ada data. Buat transaksi untuk memulai.
                  </span>
                }
                pagination
                paginationServer
                paginationComponent={(props) => (
                  <PMBPagination
                    rowCount={dataSummary?.totalItems}
                    currentPage={dataSummary?.currentPage}
                    rowsPerPage={sizeSummary ?? 10}
                    setCurrentPage={(e) => {
                      setPageSummary(e)
                    }}
                    setRowsPerPage={(e) => {
                      setSizeSummary(e)
                    }}
                    props={props}
                  />
                )}
              />
            </div>
          )}
        </ModalBody>
      </Modal>
    </>
  )
}

export default InventorySummaryCard
